import qs from "qs";

/**
 * 將物件轉換為 URL 查詢字串，保留 null 為空字串，並支援重複陣列格式。
 *
 * @param {object} params - 欲轉換的物件
 * @returns {string} - 格式化後的查詢字串（不含問號）
 *
 * @example
 * stringifyQuery({ clubID: null, type: "A", tags: ["x", "y"] })
 * // "clubID=&type=A&tags=x&tags=y"
 */
export const stringifyQuery = params => {
	return qs.stringify(params, {
		arrayFormat: "repeat",
		filter: (prefix, value) => {
			// 將 null 和 undefined 統一轉為空字串（保留 key）
			return value == null ? "" : value;
		},
	});
};

/**
 * 將查詢字串（query string）解析為單層物件，並保留空字串陣列。
 *
 * ✅ 支援以下特性：
 * - 自動去除 `?` 開頭（透過 `ignoreQueryPrefix: true`）
 * - 保留鍵值為空的項目（如 `?a=&b=123` → `{ a: "", b: "123" }`）
 * - 若鍵值為陣列（例如 `?tags=&tags=`），會保留空字串陣列：
 *   - 若全部為空 → 只保留一個空字串：`[""]`
 *   - 若含有效值 → 完整保留，如：`["x", "", ""]`
 *
 * 🚫 不支援巢狀結構（因為未啟用 deep parsing），所有欄位皆為「扁平鍵值」
 *
 * @param {string} queryString - 查詢字串，例如 "?a=1&b=&c=x&c=&c="
 * @returns {Record<string, string | string[]>} - 解析後的物件（值可能為字串或字串陣列）
 *
 * @example
 * parseQueryString("?clubID=&type=A&tags=x&tags=&tags=")
 * // =>
 * // {
 * //   clubID: "",
 * //   type: "A",
 * //   tags: ["x", "", ""]
 * // }
 *
 * parseQueryString("?tags=&tags=")
 * // =>
 * // {
 * //   tags: [""] // 若全為空，保留一個空字串
 * // }
 */
export const parseQueryString = queryString => {
	const parsed = qs.parse(queryString, {
		ignoreQueryPrefix: true,
		allowDots: true,
	});

	for (const key in parsed) {
		const value = parsed[key];

		if (Array.isArray(value)) {
			const allEmpty = value.every(v => v === "");
			parsed[key] = allEmpty ? [""] : value;
		}
	}

	// @ts-ignore 忽略型別推斷問題（不影響實際使用）
	return parsed;
};

/**
 * 將 location.search（或任意查詢字串）格式化為標準查詢字串（不含 ?）
 * - 可處理空值、重複陣列，並保留所有鍵
 *
 * @param {string} search - location.search 或其他查詢字串（例如 "?a=1&b=&tags=x&tags="）
 * @returns {string} - 格式化後的查詢字串（例如 "a=1&b=&tags=x&tags="）
 *
 * @example
 * formatLocationSearch("?a=1&b=&tags=x&tags=")
 * // "a=1&b=&tags=x&tags="
 */
export const formatLocationSearch = (search = "") => {
	const parsed = parseQueryString(search);
	return stringifyQuery(parsed);
};
